/* eslint-disable no-undef */
import { SetStateAction, Dispatch, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useServiceAreas } from 'providers/ServiceAreasProvider'
import { Polygon as GooglePolygon, InfoBox } from '@react-google-maps/api'
import { DeliveryArea } from 'types/model/serviceAreas'
import { getPolygonColor, getPolygonCentroid } from './utils'

type PolygonProps = {
  group: DeliveryArea
  deliveryAreaId: number | null
  setDrawnPath: Dispatch<SetStateAction<{ lat: number; lng: number }[][] | []>>
  setIsEditing: Dispatch<SetStateAction<boolean>>
  setDeliveryAreaId: Dispatch<SetStateAction<number | null>>
  drawnPath: { lat: number; lng: number }[][] | []
}

export const Polygon = ({
  drawnPath,
  group,
  deliveryAreaId,
  setDrawnPath,
  setIsEditing,
  setDeliveryAreaId,
}: PolygonProps) => {
  const [editable, setEditable] = useState(false)
  const { t } = useTranslation()
  const polygonRef = useRef<GooglePolygon>(null)
  const {
    allDeliveryAreas,
    setCoordinates,
    selectedDeliveryArea,
    selectedCollectionPoint,
  } = useServiceAreas()

  const handlePolygonVerticesChange = (event: google.maps.PolyMouseEvent) => {
    if (polygonRef.current && editable) {
      let currentIndex = event.vertex || event.edge
      const polygonData = drawnPath[0] || polygonRef.current.props.paths
      if ((event.edge === 0 || event.edge) && event.latLng) {
        currentIndex = event.edge + 1
        const newLat =
          event.latLng.lat() * 2 - polygonData[currentIndex - 1].lat
        const newlng =
          event.latLng.lng() * 2 - polygonData[currentIndex - 1].lng
        polygonData.splice(currentIndex, 0, { lng: newlng, lat: newLat })
      }
      if ((event.vertex === 0 || event.vertex) && event.latLng) {
        currentIndex = event.vertex
        polygonData[currentIndex] = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        }
      }
      setDrawnPath([polygonData])
    }
  }

  const handleStartEditingPolygon = (group: DeliveryArea) => {
    setIsEditing(true)
    setDeliveryAreaId(group.id || -1)
    setCoordinates([[group]])
    setEditable(true)
  }

  const paths = group.area.map((coordinate: number[]) => {
    return {
      lat: Number(coordinate[0]),
      lng: Number(coordinate[1]),
    }
  })

  const polygonColor = getPolygonColor(
    group.deliveryAreaGroupId,
    allDeliveryAreas
  )

  const centerCoordinate = getPolygonCentroid(paths)

  return (
    <div
      key={(group.id || 'NoId') + group.deliveryAreaGroupName}
      data-cy={`Polygon ${paths.length}`}>
      <GooglePolygon
        ref={polygonRef}
        editable={editable}
        onClick={() => {
          if (
            !editable ||
            (!selectedDeliveryArea.id && !selectedCollectionPoint.id)
          ) {
            handleStartEditingPolygon(group)
          }
        }}
        onMouseUp={handlePolygonVerticesChange}
        options={{
          clickable:
            Boolean(deliveryAreaId !== group.id || deliveryAreaId !== -1) &&
            Boolean(selectedDeliveryArea.id || selectedCollectionPoint.id),
          fillOpacity: 0.35,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          strokeColor: polygonColor,
          fillColor: polygonColor,
        }}
        paths={paths}
      />
      {group.id && group.id !== -1 && (
        <InfoBox
          position={
            new google.maps.LatLng(centerCoordinate[0], centerCoordinate[1])
          }
          options={{ pixelOffset: new google.maps.Size(-20, -10) }}>
          <>
            <div aria-label={t('deliveryAreaId')}>{group.id}</div>
            <div aria-label={t('deliveryAreaLabel')}>{group.label ?? ''}</div>
          </>
        </InfoBox>
      )}
      {(group.id === -1 || !group.id) && group.label && (
        <InfoBox
          position={
            new google.maps.LatLng(centerCoordinate[0], centerCoordinate[1])
          }
          options={{ pixelOffset: new google.maps.Size(-20, -10) }}>
          <div aria-label={t('deliveryAreaLabel')}>{group.label ?? ''}</div>
        </InfoBox>
      )}
    </div>
  )
}
